/**
 * CreatorConversationsPage Redux duck.
 *
 * Thin wrapper over the shared conversations factory (see
 * src/ducks/conversations.shared.js). The shared factory owns the
 * common state shape, action types, reducer, action creators, generic
 * thunks (archive/unarchive/delete/leave/etc), and base selectors.
 *
 * This file only adds creator-specific bits:
 *  - fetchSendbirdToken (uses creator profile image for avatar)
 *  - selectChannelsByType (creator-side grouping: live rooms, episodes,
 *    team, dms)
 *  - selectAllUniqueMembers (pool drawn purely from channel members)
 *  - loadData
 */

import { CHANNEL_TYPES } from '../../util/sendbird';
import { sendbirdAPI } from '../../util/api';
import { decodeChannelId } from '../../util/channelUrl';
import { createSlug } from '../../util/urlHelpers';
import { availabilityExceptionsAPI } from '../../api/clients/availabilityExceptionsAPI';
import config from '../../config';
import { assignmentsAPI } from '../../api';
import { isAssignmentEffective } from '../../util/assignments';
import { createConversationsDuck } from '../../ducks/conversations.shared';

const NAMESPACE = 'CreatorConversationsPage';
const duck = createConversationsDuck(NAMESPACE);

// ============== Creator-specific: episode options for the composer ============== //
// Lets an operator insert an "episode card" into a thread. Options are the
// creator's own scheduled live-room sessions (availability exceptions).
const SET_EPISODE_OPTIONS = `app/${NAMESPACE}/SET_EPISODE_OPTIONS`;
const setEpisodeOptions = episodeOptions => ({ type: SET_EPISODE_OPTIONS, payload: episodeOptions });

// ============== Creator-specific: audience-sovereignty banners ============== //
// Map of liveRoomId -> { organizationName } for the creator's Live Rooms that
// are under an *effective* Gig Boost assignment with temporary audience access.
// Drives the "[Org] has temporary read-only access · Revoke" thread-header
// banner so the creator sees, in the room itself, who can reach their audience.
const SET_SOVEREIGNTY_BANNERS = `app/${NAMESPACE}/SET_SOVEREIGNTY_BANNERS`;
const setSovereigntyBanners = banners => ({ type: SET_SOVEREIGNTY_BANNERS, payload: banners });

// ============== Re-export shared action types ============== //
export const {
  SET_SENDBIRD_CONNECTED,
  FETCH_TOKEN_REQUEST,
  FETCH_TOKEN_SUCCESS,
  FETCH_TOKEN_ERROR,
  SET_CHANNELS,
  ADD_CHANNELS,
  ADD_CHANNEL,
  UPDATE_CHANNEL,
  UPDATE_CHANNELS,
  REMOVE_CHANNEL,
  REMOVE_CHANNELS,
  SET_HAS_MORE_CHANNELS,
  SET_SELECTED_CHANNEL,
  SET_MESSAGES,
  ADD_MESSAGE,
  ADD_MESSAGES,
  PREPEND_MESSAGES,
  UPDATE_MESSAGE,
  UPDATE_MESSAGES,
  REMOVE_MESSAGE,
  REMOVE_MESSAGES,
  SET_HAS_MORE_MESSAGES,
  SET_LOADING_MORE_MESSAGES,
  SET_THREAD_MESSAGES,
  ADD_THREAD_MESSAGE,
  SET_ACTIVE_THREAD,
  SET_THREAD_LOADING,
  SET_ARCHIVE_IN_PROGRESS,
  SET_UNARCHIVE_IN_PROGRESS,
  SET_DELETE_IN_PROGRESS,
  SET_CHANNEL_INFO_OPEN,
  SET_ACTIVE_TAB,
  SET_CHANNEL_MEMBERS,
  SET_CHANNEL_BOOKINGS,
  SET_PENDING_CHANNEL_URL,
  FETCH_CHANNELS_REQUEST,
  FETCH_CHANNELS_SUCCESS,
  FETCH_CHANNELS_ERROR,
  FETCH_MESSAGES_REQUEST,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_ERROR,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_ERROR,
  CREATE_CHANNEL_REQUEST,
  CREATE_CHANNEL_SUCCESS,
  CREATE_CHANNEL_ERROR,
} = duck.types;

// ============== Reducer ============== //
// Wrap the shared reducer to also hold creator-specific episodeOptions.
const reducer = (state, action = {}) => {
  let base = duck.reducer(state, action);
  if (action.type === SET_EPISODE_OPTIONS) {
    base = { ...base, episodeOptions: action.payload };
  }
  if (action.type === SET_SOVEREIGNTY_BANNERS) {
    base = { ...base, sovereigntyBanners: action.payload };
  }
  if (base.episodeOptions === undefined) base = { ...base, episodeOptions: [] };
  if (base.sovereigntyBanners === undefined) base = { ...base, sovereigntyBanners: {} };
  return base;
};
export default reducer;

// ============== Episode options selector ============== //
export const selectEpisodeOptions = state => state[NAMESPACE]?.episodeOptions || [];

// ============== Sovereignty banners selector ============== //
export const selectSovereigntyBanners = state =>
  state[NAMESPACE]?.sovereigntyBanners || {};

// ============== Re-export shared action creators ============== //
export const {
  setSendbirdConnected,
  setChannels,
  addChannels,
  addChannel,
  updateChannel,
  updateChannels,
  removeChannel,
  removeChannels,
  setHasMoreChannels,
  setSelectedChannel,
  setMessages,
  addMessage,
  addMessages,
  prependMessages,
  updateMessage,
  updateMessages,
  removeMessage,
  removeMessages,
  setHasMoreMessages,
  setLoadingMoreMessages,
  setThreadMessages,
  addThreadMessage,
  setActiveThread,
  setThreadLoading,
  setArchiveInProgress,
  setUnarchiveInProgress,
  setDeleteInProgress,
  setChannelInfoOpen,
  setActiveTab,
  setChannelMembers,
  setChannelBookings,
  setPendingChannelUrl,
} = duck.actions;

// ============== Re-export shared thunks ============== //
export const {
  fetchChannelByUrl,
  fetchChannels,
  createChannel,
  archiveChannel,
  unarchiveChannel,
  deleteChannel,
  leaveChannel,
  addChannelMembers,
  removeChannelMember,
} = duck.thunks;

// ============== Re-export shared selectors ============== //
export const {
  selectSendbirdConnected,
  selectTokenLoading,
  selectTokenError,
  selectSendbirdToken,
  selectChannels,
  selectHasMoreChannels,
  selectSelectedChannel,
  selectMessages,
  selectMessagesLoading,
  selectHasMoreMessages,
  selectLoadingMoreMessages,
  selectActiveTab,
  selectChannelInfoOpen,
  selectActiveThreadMessageId,
  selectThreadMessages,
  selectThreadLoading,
  selectArchiveInProgress,
  selectUnarchiveInProgress,
  selectDeleteInProgress,
  selectChannelMembers,
  selectChannelBookings,
  selectPendingChannelUrl,
  selectTotalUnreadCount,
} = duck.selectors;

// ============== Creator-specific thunks ============== //

/**
 * Fetch Sendbird session token. Uses the creator's own profile image as
 * the Sendbird avatar so messages they send show the right picture.
 */
export const fetchSendbirdToken = () => async (dispatch, getState) => {
  dispatch({ type: FETCH_TOKEN_REQUEST });
  try {
    const currentUser = getState().user.currentUser;
    const creatorProfileImage =
      currentUser?.attributes?.profile?.publicData?.profileImage?.link;

    const response = await sendbirdAPI.getToken({
      profileUrl: creatorProfileImage || undefined,
    });
    dispatch({ type: FETCH_TOKEN_SUCCESS, payload: response });
    return response;
  } catch (error) {
    console.error('Fetch token error:', error);
    dispatch({ type: FETCH_TOKEN_ERROR, payload: error.message });
    throw error;
  }
};

// ============== Creator-specific selectors ============== //

export const selectChannelsByType = state => {
  const channels = selectChannels(state) || [];
  const grouped = { liveRooms: [], episodes: [], team: [], dms: [] };
  channels.forEach(channel => {
    switch (channel.customType) {
      case CHANNEL_TYPES.LIVE_ROOM:
        grouped.liveRooms.push(channel);
        break;
      case CHANNEL_TYPES.EPISODE:
        grouped.episodes.push(channel);
        break;
      case CHANNEL_TYPES.TEAM:
        grouped.team.push(channel);
        break;
      case CHANNEL_TYPES.DM:
        grouped.dms.push(channel);
        break;
      default:
        grouped.team.push(channel);
    }
  });
  return grouped;
};

/**
 * For the DM picker: every distinct user the creator currently shares a
 * channel with (minus themselves). Returned in react-select shape.
 */
export const selectAllUniqueMembers = (state, currentUserId) => {
  const channels = selectChannels(state) || [];
  const membersMap = new Map();

  channels.forEach(channel => {
    (channel.members || []).forEach(member => {
      if (member.userId === currentUserId) return;
      if (membersMap.has(member.userId)) return;
      membersMap.set(member.userId, {
        value: member.userId,
        label: member.nickname || member.userId,
        avatar: member.profileUrl || null,
        ...member,
      });
    });
  });

  return Array.from(membersMap.values()).sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase())
  );
};

// ============== loadData ============== //

// Fetch the creator's own scheduled episodes (live-room availability
// exceptions) and expose them as flat options for the composer's
// "Insert episode" picker. Best-effort: failures leave the list empty.
export const fetchEpisodeOptions = () => async (dispatch, getState, sdk) => {
  try {
    const listingsResponse = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
    });
    const listings = listingsResponse?.data?.data || [];
    if (listings.length === 0) {
      dispatch(setEpisodeOptions([]));
      return;
    }

    const now = new Date();
    const start = new Date(now);
    start.setMonth(start.getMonth() - 1);
    const end = new Date(now);
    end.setMonth(end.getMonth() + 9);

    const options = [];
    for (const listing of listings) {
      const listingId = listing?.id?.uuid;
      const listingTitle = listing?.attributes?.title || 'Untitled';
      const listingSlug = createSlug(listingTitle);
      const exceptionsMeta = listing?.attributes?.publicData?.exceptions || [];
      const membershipTiers = listing?.attributes?.publicData?.membershipTiers || [];
      try {
        const exceptionsResponse = await availabilityExceptionsAPI.query({
          listingId,
          start: start.toISOString(),
          end: end.toISOString(),
        });
        const exceptions = exceptionsResponse?.data?.data?.data || [];
        exceptions.forEach(exception => {
          const exceptionId = exception?.id?.uuid;
          const { start: exStart, end: exEnd } = exception?.attributes || {};
          // Only surface upcoming sessions.
          if (!exEnd || new Date(exEnd) < now) return;
          const metadata = exceptionsMeta.find(meta => meta.id === exceptionId);
          const title = metadata?.attributes?.title || listingTitle;
          const access = metadata?.attributes?.access || 'free';
          const membershipTierId = metadata?.attributes?.membershipTierId || null;
          const tier = membershipTiers.find(t => t.id === membershipTierId);
          const accessTier = tier?.name || (access !== 'free' ? access : null);
          options.push({
            id: exceptionId,
            listingId,
            listingSlug,
            title,
            start: exStart ? new Date(exStart).toISOString() : null,
            end: exEnd ? new Date(exEnd).toISOString() : null,
            accessTier,
          });
        });
      } catch (e) {
        // Skip this listing but continue with the rest.
        console.error(`Error fetching episodes for listing ${listingId}:`, e);
      }
    }

    options.sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
    dispatch(setEpisodeOptions(options));
  } catch (e) {
    console.error('Error fetching episode options:', e);
    dispatch(setEpisodeOptions([]));
  }
};

// Fetch the creator's assignments and derive the thread-header sovereignty
// banners: one per Live Room that is under an *effective* assignment whose
// creator granted temporary audience access. Keyed by liveRoomId so MessageArea
// can match a live_room channel to its org. Best-effort.
export const fetchSovereigntyBanners = () => async dispatch => {
  try {
    const res = await assignmentsAPI.listForCreator();
    const body = res?.data ?? res ?? {};
    const assignments = (body?.data ?? body)?.assignments || [];
    const banners = {};
    assignments.forEach(a => {
      if (
        a?.liveRoomId &&
        isAssignmentEffective(a) &&
        a?.grantedConsents?.temporaryAudienceAccess === true
      ) {
        banners[a.liveRoomId] = {
          organizationName: a.organizationName || 'An organization',
          assignmentId: a.id,
        };
      }
    });
    dispatch(setSovereigntyBanners(banners));
  } catch (e) {
    console.error('Error fetching sovereignty banners:', e);
    dispatch(setSovereigntyBanners({}));
  }
};

export const loadData = params => async dispatch => {
  const { channelId } = params;
  if (channelId) {
    dispatch(setPendingChannelUrl(decodeChannelId(channelId)));
  } else {
    dispatch(setPendingChannelUrl(null));
  }
  // Fire-and-forget; must not block the SendBird token.
  dispatch(fetchEpisodeOptions());
  dispatch(fetchSovereigntyBanners());
  return dispatch(fetchSendbirdToken());
};
